import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useWorkflowStore } from '../../stores';
import FlowCanvas from '../../components/flow/FlowCanvas';
import { getWorkflow, updateWorkflow, executeWorkflow } from '../../services/workflowService';


// 格式化时间
const formatTime = (timestamp: number | string) => {
  return new Date(timestamp).toLocaleString('zh-CN', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export default function WorkflowEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { 
    currentWorkflow, 
    setCurrentWorkflow, 
    workflows,
    updateWorkflow: updateWorkflowInStore 
  } = useWorkflowStore();
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'unsaved'>('unsaved');

  // 加载工作流数据
  useEffect(() => {
    if (!id) {
      navigate('/workflow');
      return;
    }

    const loadWorkflow = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // 先尝试从 store 中获取
        const existingWorkflow = workflows.find(w => w.id === id);
        if (existingWorkflow) {
          setCurrentWorkflow(existingWorkflow);
          setIsLoading(false);
          return;
        }

        // 从 API 获取
        const workflow = await getWorkflow(id);
        setCurrentWorkflow(workflow);
      } catch (err) {
        console.error('加载工作流失败:', err);
        setError(err instanceof Error ? err.message : '加载工作流失败');
      } finally {
        setIsLoading(false);
      }
    };

    loadWorkflow();

    // 清理函数
    return () => {
      // 可选：离开页面时清理当前工作流
      // setCurrentWorkflow(null);
    };
  }, [id, navigate, setCurrentWorkflow, workflows]);

  // 自动保存监听
  useEffect(() => {
    if (!currentWorkflow) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (saveStatus === 'unsaved') {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [saveStatus, currentWorkflow]);

  // 保存工作流
  const handleSave = useCallback(async () => {
    if (!currentWorkflow || !id) return;

    setIsSaving(true);
    setSaveStatus('saving');
    setError(null);
    
    try {
      // 只发送后端需要的字段，排除 id, createdAt, updatedAt 等自动管理的字段
      const { id: workflowId, createdAt, updatedAt, version, isActive, ...updateData } = currentWorkflow;
      await updateWorkflow(id, updateData);
      updateWorkflowInStore(currentWorkflow.id, currentWorkflow);
      setSaveStatus('saved');
      
      // 3秒后清除保存状态提示
      setTimeout(() => {
        setSaveStatus('saved');
      }, 2000);
    } catch (err) {
      console.error('保存失败:', err);
      // 401错误需要提示用户重新登录
      if (err instanceof Error && err.message.includes('401')) {
        setError('登录已过期，请重新登录后再保存');
        setSaveStatus('unsaved');
        // 可选：自动跳转到登录页
        // window.location.href = '/login';
      } else {
        // 其他错误：本地存储作为后备，但提示用户
        updateWorkflowInStore(currentWorkflow.id, currentWorkflow);
        setError(err instanceof Error ? `${err.message}（数据已保存到本地）` : '保存失败（数据已保存到本地）');
        setSaveStatus('unsaved');
      }
    } finally {
      setIsSaving(false);
    }
  }, [currentWorkflow, id, updateWorkflowInStore]);

  // 运行工作流
  const handleRun = useCallback(async () => {
    if (!id) return;

    setIsRunning(true);
    setError(null);
    try {
      const result = await executeWorkflow(id);
      // 可以在这里显示运行结果或跳转到执行详情页
      alert(`工作流已开始执行，执行ID: ${result.executionId}`);
    } catch (err) {
      console.error('运行失败:', err);
      // 401错误不显示错误提示，因为API拦截器已经处理了
      if (err instanceof Error && err.message.includes('401')) {
        // 静默处理401错误
      } else {
        setError(err instanceof Error ? err.message : '运行失败');
      }
    } finally {
      setIsRunning(false);
    }
  }, [id]);

  // 键盘快捷键
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl/Cmd + S 保存
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleSave]);

  // 加载中状态
  if (isLoading) {
    return (
      <div className="h-screen flex flex-col bg-gray-50">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4" />
            <p className="text-gray-500">加载工作流...</p>
          </div>
        </div>
      </div>
    );
  }

  // 错误状态
  if (error) {
    return (
      <div className="h-screen flex flex-col bg-gray-50">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center max-w-md mx-auto px-6">
            <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">加载失败</h3>
            <p className="text-gray-500 mb-6">{error}</p>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => navigate('/workflow')}
                className="px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                返回列表
              </button>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                重试
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* 编辑器头部 */}
      <header className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* 返回按钮 */}
            <button
              onClick={() => navigate('/workflow')}
              className="p-2 hover:bg-gray-100 rounded-lg text-gray-600 transition-colors"
              title="返回列表 (Esc)"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>

            {/* 工作流信息 */}
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <h1 className="font-semibold text-gray-900">
                  {currentWorkflow?.name || '未命名工作流'}
                </h1>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span>
                    {currentWorkflow ? `${currentWorkflow.nodes.length} 节点 · ${currentWorkflow.edges.length} 连接` : ''}
                  </span>
                  <span>·</span>
                  <span>
                    {currentWorkflow ? `编辑于 ${formatTime(currentWorkflow.updatedAt)}` : ''}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* 右侧操作按钮 */}
          <div className="flex items-center gap-3">
            {/* 保存状态 */}
            <span className={`text-xs ${
              saveStatus === 'saved' ? 'text-green-600' : 
              saveStatus === 'saving' ? 'text-blue-600' : 'text-yellow-600'
            }`}>
              {saveStatus === 'saved' ? '✓ 已保存' : 
               saveStatus === 'saving' ? '保存中...' : '未保存'}
            </span>

            {/* 保存按钮 */}
            <button
              onClick={handleSave}
              disabled={isSaving}
              className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                isSaving
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
              title="保存 (Ctrl+S)"
            >
              {isSaving ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  保存中
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                  </svg>
                  保存
                </>
              )}
            </button>

            {/* 运行按钮 */}
            <button
              onClick={handleRun}
              disabled={isRunning}
              className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                isRunning
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
            >
              {isRunning ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  运行中
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  运行
                </>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* 编辑器主体 - 复用现有的 FlowCanvas */}
      <div className="flex-1 overflow-hidden">
        <FlowCanvas />
      </div>
    </div>
  );
}
